import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StudentNavigation from '../../components/ui/StudentNavigation';
import InstituteNavigation from '../../components/ui/InstituteNavigation';
import WaitingRoom from './components/WaitingRoom';
import JitsiMeetRoom from '../../components/JitsiMeetRoom';
import { useAuth } from '../../contexts/AuthContext';
import { sessionService } from '../../services/sessionService';
import { supabase } from '../../lib/supabase';

const VideoCounseling = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams?.get('session');
  const { user, userProfile } = useAuth();
  const [sessionState, setSessionState] = useState('list'); // 'list' | 'waiting' | 'active'
  const [currentSession, setCurrentSession] = useState(null);
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(null);

  const currentUser = {
    id: user?.id || 'user-1',
    name: userProfile?.name || 'User',
    role: userProfile?.role || 'student'
  };

  // Check video_enabled for institute role
  useEffect(() => {
    if (!user || !userProfile) return;
    const checkVideoAccess = async () => {
      if (userProfile?.role === 'institute') {
        try {
          const { data, error } = await supabase
            ?.from('institute_profiles')
            ?.select('video_enabled')
            ?.eq('user_id', user?.id)
            ?.single();
          if (error) throw error;
          setVideoEnabled(data?.video_enabled ?? false);
        } catch (err) {
          setVideoEnabled(false);
        }
      } else {
        setVideoEnabled(true);
      }
    };
    checkVideoAccess();
  }, [user, userProfile]);

  // Load sessions
  useEffect(() => {
    if (!user || videoEnabled === null) return;
    if (!videoEnabled && userProfile?.role === 'institute') { setLoading(false); return; }

    const loadSessions = async () => {
      try {
        const sessions = await sessionService?.getSessions(user?.id, userProfile?.role || 'student');
        setAllSessions(sessions || []);

        let activeSession = null;
        if (sessionIdParam) {
          activeSession = sessions?.find(s => s?.id === sessionIdParam || String(s?.id) === String(sessionIdParam));
        }
        if (!activeSession) {
          activeSession = sessions?.find(s => s?.status === 'ongoing');
        }

        if (activeSession) {
          setCurrentSession(activeSession);
          if (activeSession?.status === 'ongoing') {
            setSessionState('active');
          } else {
            setSessionState('list');
          }
        } else {
          setSessionState('list');
        }
      } catch (err) {
        console.error('Error loading sessions:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, [user, userProfile, videoEnabled, sessionIdParam]);

  const getSessionTitle = (session) => {
    if (!session) return null;
    if (session?.topic) return session?.topic;
    if (session?.sessionType === 'one_on_one') return 'One-on-One Counseling Session';
    if (session?.sessionType === 'group_presentation') return 'Group Presentation Session';
    return 'Video Counseling Session';
  };

  const getInstituteName = (session) => {
    if (!session) return null;
    return session?.institute?.name || session?.instituteName || null;
  };

  const formatSessionDate = (session) => {
    const dateStr = session?.scheduledAt || session?.bookingDate;
    if (!dateStr) return 'Date TBD';
    try {
      const date = new Date(dateStr);
      return date?.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatSessionTime = (session) => {
    const dateStr = session?.scheduledAt;
    const timeStr = session?.bookingTime;
    if (timeStr) {
      try {
        const [h, m] = timeStr?.split(':');
        const d = new Date();
        d?.setHours(parseInt(h), parseInt(m));
        return d?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      } catch { return timeStr; }
    }
    if (dateStr) {
      try {
        return new Date(dateStr)?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      } catch { return ''; }
    }
    return '';
  };

  const getStatusBadge = (status) => {
    const map = {
      confirmed: { label: 'Confirmed', cls: 'bg-success/10 text-success border border-success/20' },
      scheduled: { label: 'Scheduled', cls: 'bg-primary/10 text-primary border border-primary/20' },
      pending: { label: 'Pending', cls: 'bg-warning/10 text-warning border border-warning/20' },
      ongoing: { label: 'Live Now', cls: 'bg-error/10 text-error border border-error/20 animate-pulse' },
    };
    return map?.[status] || { label: status, cls: 'bg-muted text-muted-foreground border border-border' };
  };

  const handleJoinSession = (session) => {
    setCurrentSession(session);
    if (session?.status === 'ongoing') {
      setSessionState('active');
    } else {
      setSessionState('waiting');
    }
  };

  const sessionInfo = currentSession ? {
    id: currentSession?.id,
    title: getSessionTitle(currentSession),
    instituteName: getInstituteName(currentSession),
    scheduledTime: currentSession?.scheduledAt ? new Date(currentSession.scheduledAt) : new Date(),
    estimatedWaitTime: '5-10 minutes',
    waitingPosition: 1
  } : null;

  const jitsiRoomName = currentSession
    ? `StudyExpo-Session-${currentSession?.id}`
    : null;

  useEffect(() => {
    if (!currentSession || sessionState !== 'waiting') return;
    const timer = setTimeout(() => {
      setSessionState('active');
      sessionService?.updateSessionStatus(currentSession?.id, 'ongoing')?.catch(console.error);
    }, 3000);
    return () => clearTimeout(timer);
  }, [sessionState, currentSession]);

  const handleEndSession = async () => {
    if (window.confirm('Are you sure you want to end this session?')) {
      if (currentSession) {
        try {
          await sessionService?.updateSessionStatus(currentSession?.id, 'completed');
        } catch (err) {
          console.error('Error ending session:', err);
        }
      }
      setCurrentSession(null);
      setSessionState('list');
    }
  };

  const handleCancelWaiting = () => {
    setCurrentSession(null);
    setSessionState('list');
  };

  const userRole = userProfile?.role || 'student';
  const isInstitute = userRole === 'institute';

  if (loading || videoEnabled === null) {
    return (
      <>
        {isInstitute ? <InstituteNavigation /> : <StudentNavigation />}
        <div className="pt-[60px] flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </>
    );
  }

  if (isInstitute && !videoEnabled) {
    return (
      <div className="min-h-screen bg-background">
        <InstituteNavigation />
        <div className="pt-[60px] flex items-center justify-center min-h-screen">
          <div className="max-w-md w-full mx-4 text-center">
            <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">Video Counseling Not Activated</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">Video counseling not activated. Please contact admin.</p>
              <button onClick={() => navigate('/institute-dashboard')} className="mt-6 px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const instituteProfile = userProfile?.institute_profiles?.[0];
  const isInstituteApproved = !isInstitute || instituteProfile?.approval_status === 'approved';

  if (!isInstituteApproved) {
    return (
      <div className="min-h-screen bg-background">
        <InstituteNavigation />
        <div className="pt-[60px] flex items-center justify-center min-h-screen">
          <div className="max-w-md w-full mx-4 text-center">
            <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">Access Restricted</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">Your account is pending admin approval. Chat and video counseling will be available once approved.</p>
              <button onClick={() => navigate('/institute-dashboard')} className="mt-6 px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Waiting room
  if (sessionState === 'waiting') {
    return (
      <>
        <StudentNavigation />
        <div className="pt-[60px]">
          <WaitingRoom
            instituteName={sessionInfo?.instituteName}
            sessionTitle={sessionInfo?.title}
            estimatedWaitTime={sessionInfo?.estimatedWaitTime}
            position={sessionInfo?.waitingPosition}
            onCancel={handleCancelWaiting}
          />
        </div>
      </>
    );
  }

  // Active Jitsi session
  if (sessionState === 'active' && currentSession) {
    return (
      <>
        <StudentNavigation />
        <div className="pt-[60px] h-screen flex flex-col bg-gray-900">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-success rounded-full animate-pulse" />
              <div>
                <p className="text-white font-semibold text-sm">{sessionInfo?.title}</p>
                <p className="text-gray-400 text-xs">{sessionInfo?.instituteName}</p>
              </div>
            </div>
            <button
              onClick={handleEndSession}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-error hover:bg-error/80 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07C9.44 17.25 7.76 15.57 6.06 13.32a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
                <line x1="23" y1="1" x2="1" y2="23"/>
              </svg>
              End Session
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <JitsiMeetRoom
              roomName={jitsiRoomName}
              displayName={currentUser?.name}
              title={sessionInfo?.title}
              onClose={handleEndSession}
            />
          </div>
        </div>
      </>
    );
  }

  // Sessions list view (default for students)
  const upcomingSessions = allSessions?.filter(s => ['confirmed', 'scheduled', 'pending', 'ongoing']?.includes(s?.status));

  return (
    <>
      <StudentNavigation />
      <div className="pt-[60px] min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">Video Counseling</h1>
                <p className="text-muted-foreground text-sm">Your scheduled sessions with institutes</p>
              </div>
            </div>
          </div>

          {upcomingSessions?.length === 0 ? (
            <div className="bg-card rounded-xl shadow-sm border border-border p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">No Sessions Booked</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-sm mx-auto">
                You don't have any upcoming video counseling sessions. Visit an institute's virtual booth to book a session.
              </p>
              <button
                onClick={() => navigate('/exhibition-hall')}
                className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
              >
                Explore Institutes
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingSessions?.map((session) => {
                const badge = getStatusBadge(session?.status);
                const isLive = session?.status === 'ongoing';
                return (
                  <div
                    key={session?.id}
                    className={`bg-card rounded-xl border p-6 shadow-sm transition-all ${isLive ? 'border-error/40 shadow-error/10' : 'border-border hover:border-primary/30 hover:shadow-md'}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Status badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge?.cls}`}>
                            {isLive && <span className="w-1.5 h-1.5 bg-error rounded-full animate-pulse" />}
                            {badge?.label}
                          </span>
                          {isLive && (
                            <span className="text-xs text-error font-medium">Session is live — join now!</span>
                          )}
                        </div>

                        {/* Session title */}
                        <h3 className="text-base font-semibold text-foreground mb-1 truncate">
                          {getSessionTitle(session)}
                        </h3>

                        {/* Institute name */}
                        {getInstituteName(session) && (
                          <div className="flex items-center gap-1.5 mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                              <polyline points="9 22 9 12 15 12 15 22"/>
                            </svg>
                            <span className="text-sm text-muted-foreground font-medium">{getInstituteName(session)}</span>
                          </div>
                        )}

                        {/* Date & Time */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                              <line x1="16" y1="2" x2="16" y2="6"/>
                              <line x1="8" y1="2" x2="8" y2="6"/>
                              <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            <span>{formatSessionDate(session)}</span>
                          </div>
                          {formatSessionTime(session) && (
                            <div className="flex items-center gap-1.5">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                              </svg>
                              <span>{formatSessionTime(session)}</span>
                            </div>
                          )}
                          {session?.durationMinutes && (
                            <div className="flex items-center gap-1.5">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                              </svg>
                              <span>{session?.durationMinutes} min</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Join button */}
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => handleJoinSession(session)}
                          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            isLive
                              ? 'bg-error text-white hover:bg-error/90 shadow-lg shadow-error/20'
                              : 'bg-primary text-primary-foreground hover:bg-primary/90'
                          }`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="23 7 16 12 23 17 23 7"/>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                          </svg>
                          {isLive ? 'Join Now' : 'Join Session'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Back to dashboard */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/student-dashboard')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VideoCounseling;